import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * Kriging preparation panel (stepper, waterline list, and action buttons).
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:INTERTIDAL
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  root: {
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    margin: 12,
    marginTop: 40,
  },
  card: {
    pointerEvents: 'auto',
  },
  stepContent: {
    marginTop: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
  },
  colorDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '1px solid #666',
    flexShrink: 0,
  },
  listItem: {
    borderRadius: 4,
    marginBottom: 2,
  },
}));

/**
 * @function KrigingPanel
 * @description React component that guides the user through preparing the Kriging interpolation
 * in two steps: (1) reviewing/resetting waterlines removed from the analysis, and (2) selecting
 * which dated waterline layers to include, before confirming the interpolation.
 * @param {Object} props - Component properties.
 * @param {number} props.step - Current wizard step (1 or 2).
 * @param {Array<Object>} props.waterlineLayers - Waterline layers available for interpolation (date, tideValue, color).
 * @param {number} props.removedCount - Number of waterlines removed from the current analysis.
 * @param {Set<number>} props.selectedDates - Indices (into waterlineLayers) currently selected.
 * @param {Function} props.onToggleDate - Called with a layer index to toggle its selection.
 * @param {Function} props.onResetRemovals - Called to restore previously removed waterlines.
 * @param {Function} props.onNext - Called to advance from step 1 to step 2.
 * @param {Function} props.onBack - Called to return from step 2 to step 1.
 * @param {Function} props.onCancel - Called to cancel the Kriging preparation flow.
 * @param {Function} props.onConfirm - Called to confirm the interpolation with the selected dates.
 * **Called by:
 * @see {ProcessingIntertidalPage}
 * @memberof module:INTERTIDAL
 * @returns {JSX.Element} The Kriging preparation panel.
 */
const KrigingPanel = ({
  step,
  waterlineLayers,
  removedCount,
  selectedDates,
  onToggleDate,
  onResetRemovals,
  onNext,
  onBack,
  onCancel,
  onConfirm,
}) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const steps = [
    t('forms.intertidal.krigingStep1'),
    t('forms.intertidal.krigingStep2'),
  ];

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('forms.intertidal.krigingPrepare')}
          </Typography>

          <Stepper activeStep={step - 1} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {step === 1 && (
            <Box className={classes.stepContent}>
              <Typography variant="body2" color="textSecondary" paragraph>
                {t('forms.intertidal.krigingStep1Desc')}
              </Typography>

              <Typography variant="body1">
                {removedCount > 0
                  ? `${removedCount} ${t('forms.intertidal.linesMarked')}`
                  : t('forms.intertidal.noLinesMarked')}
              </Typography>

              <Box className={classes.actions}>
                <Button size="small" onClick={onResetRemovals} disabled={removedCount === 0}>
                  {t('general.reset')}
                </Button>
                <Box>
                  <Button size="small" onClick={onCancel} style={{ marginRight: 8 }}>
                    {t('general.cancel')}
                  </Button>
                  <Button size="small" variant="contained" color="primary" onClick={onNext}>
                    {t('general.next')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {step === 2 && (
            <Box className={classes.stepContent}>
              <Typography variant="body2" color="textSecondary" paragraph>
                {t('forms.intertidal.krigingStep2Desc')}
              </Typography>

              <List dense>
                {waterlineLayers.map((layer, index) => {
                  const isSelected = selectedDates.has(index);
                  const dateLabel = layer.date
                    ? new Date(layer.date).toLocaleDateString()
                    : `${t('forms.intertidal.layer') || 'Layer'} ${index + 1}`;
                  const tideLabel = layer.tideValue != null ? `${layer.tideValue.toFixed(2)}m` : '';

                  return (
                    <ListItem
                      key={index}
                      button
                      className={classes.listItem}
                      onClick={() => onToggleDate(index)}
                      style={{ opacity: isSelected ? 1 : 0.4 }}
                    >
                      <ListItemIcon style={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemIcon style={{ minWidth: 24 }}>
                        <div className={classes.colorDot} style={{ backgroundColor: layer.color }} />
                      </ListItemIcon>
                      <ListItemText primary={dateLabel} secondary={tideLabel} />
                    </ListItem>
                  );
                })}
              </List>

              <Box className={classes.actions}>
                <Button size="small" onClick={onBack}>
                  {t('general.back')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={onConfirm}
                  disabled={selectedDates.size === 0}
                >
                  {t('forms.intertidal.confirmInterpolation')}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default KrigingPanel;
